import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw } from 'lucide-react';
import * as provider from '../../data/community/communityDataProvider';
import { useCommunityDataSource } from '../../utils/communityDataSource';
import ThreadPostCard from '../../components/CommunityThread/ThreadPostCard';
import ThreadDetailModal from '../../components/ThreadDetailModal/ThreadDetailModal';

const ACTING_USER_ID = 1001;

const DEFAULT_SEARCH = {
  lat: '37.4979',
  lon: '127.0276',
  radius: '10000',
  planetId: '',
  namePrefix: '',
};

const EMPTY_CREATE = {
  name: '',
  planet_id: '',
  latitude: '',
  longitude: '',
};

function BoardDetailPanel({ board, isLoading, error }) {
  if (isLoading) return <p className="text-sm text-dark-text-muted p-4">Loading board…</p>;
  if (error) return <p className="text-sm text-red-400 p-4">{error.message}</p>;
  if (!board) return null;

  return (
    <div className="bg-dark-card border border-dark-border rounded-lg p-4 space-y-2">
      <h3 className="text-lg font-semibold text-dark-text">#{board.name}</h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <dt className="text-dark-text-muted">ID</dt>
        <dd className="text-dark-text">{board.id}</dd>
        <dt className="text-dark-text-muted">board_key</dt>
        <dd className="text-dark-text font-mono">{board.board_key}</dd>
        <dt className="text-dark-text-muted">Threads</dt>
        <dd className="text-primary font-semibold">{board.thread_count ?? 0}</dd>
        <dt className="text-dark-text-muted">Planet</dt>
        <dd className="text-dark-text">{board.planet_id ?? '—'}</dd>
        <dt className="text-dark-text-muted">Hidden</dt>
        <dd className="text-dark-text">{board.hidden === 1 ? 'Yes' : 'No'}</dd>
        <dt className="text-dark-text-muted">Created</dt>
        <dd className="text-dark-text-muted">{board.created_at}</dd>
      </dl>
    </div>
  );
}

export default function CommunityBoards() {
  const [search, setSearch] = useState(DEFAULT_SEARCH);
  const [submitted, setSubmitted] = useState({
    lat: Number(DEFAULT_SEARCH.lat),
    lon: Number(DEFAULT_SEARCH.lon),
    radius: Number(DEFAULT_SEARCH.radius),
    planetId: DEFAULT_SEARCH.planetId,
  });
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const dataSource = useCommunityDataSource();
  const queryClient = useQueryClient();

  const boardsQuery = useQuery({
    queryKey: ['communityBoards', dataSource, submitted],
    queryFn: () =>
      provider.getBoards({
        latitude: submitted.lat,
        longitude: submitted.lon,
        radius_m: submitted.radius,
        planet_id: submitted.planetId || undefined,
        limit: 50,
      }),
  });

  const boardDetailQuery = useQuery({
    queryKey: ['communityBoardDetail', dataSource, selectedBoardId],
    queryFn: () => provider.getBoardById(selectedBoardId),
    enabled: !!selectedBoardId,
  });

  const boardThreadsQuery = useQuery({
    queryKey: ['communityBoardThreads', dataSource, selectedBoardId],
    queryFn: () => provider.getThreadsByBoard(selectedBoardId, { limit: 50 }),
    enabled: !!selectedBoardId,
  });

  const createMut = useMutation({
    mutationFn: () =>
      provider.postBoard(
        {
          name: createForm.name.trim(),
          planet_id: createForm.planet_id ? Number(createForm.planet_id) : undefined,
          latitude: createForm.latitude ? Number(createForm.latitude) : undefined,
          longitude: createForm.longitude ? Number(createForm.longitude) : undefined,
        },
        ACTING_USER_ID,
      ),
    onSuccess: (board) => {
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      queryClient.invalidateQueries({ queryKey: ['communityBoards'] });
      queryClient.invalidateQueries({ queryKey: ['communityOverview'] });
      if (board?.id) setSelectedBoardId(board.id);
    },
  });

  const boards = useMemo(() => {
    const rows = boardsQuery.data?.data || [];
    const prefix = search.namePrefix.trim().toLowerCase();
    if (!prefix) return rows;
    return rows.filter(
      (b) =>
        b.name?.toLowerCase().startsWith(prefix) ||
        b.board_key?.toLowerCase().startsWith(prefix),
    );
  }, [boardsQuery.data, search.namePrefix]);

  const threads = boardThreadsQuery.data?.data || [];

  const handleSearch = (e) => {
    e.preventDefault();
    setSubmitted({
      lat: Number(search.lat),
      lon: Number(search.lon),
      radius: Number(search.radius) || 10000,
      planetId: search.planetId,
    });
    setSelectedBoardId(null);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-dark-border bg-dark-card/40 px-4 py-3 text-xs text-dark-text-secondary">
        Topic boards (주제 커뮤니티): search by location via{' '}
        <code className="text-primary">GET /boards</code>, view detail{' '}
        <code className="text-primary">GET /boards/&#123;id&#125;</code>, list threads{' '}
        <code className="text-primary">GET /boards/&#123;id&#125;/threads</code>.
      </div>

      <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-dark-text-muted">Latitude</label>
          <input
            className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-32"
            value={search.lat}
            onChange={(e) => setSearch({ ...search, lat: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-dark-text-muted">Longitude</label>
          <input
            className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-32"
            value={search.lon}
            onChange={(e) => setSearch({ ...search, lon: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-dark-text-muted">Radius (m)</label>
          <input
            className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-24"
            value={search.radius}
            onChange={(e) => setSearch({ ...search, radius: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-dark-text-muted">Planet ID</label>
          <input
            className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-20"
            value={search.planetId}
            onChange={(e) => setSearch({ ...search, planetId: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-dark-text-muted">Name prefix</label>
          <input
            className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-36"
            placeholder="seoul, feed…"
            value={search.namePrefix}
            onChange={(e) => setSearch({ ...search, namePrefix: e.target.value })}
          />
        </div>
        <button type="submit" className="px-4 py-2 bg-primary text-black rounded font-medium text-sm">
          Search boards
        </button>
        <button
          type="button"
          className="p-2 border border-dark-border rounded hover:border-primary"
          onClick={() => boardsQuery.refetch()}
          title="Refresh"
        >
          <RefreshCw size={16} />
        </button>
        <button
          type="button"
          className="flex items-center gap-1 px-3 py-2 border border-dark-border rounded text-sm hover:border-primary ml-auto"
          onClick={() => setCreateOpen(true)}
        >
          <Plus size={16} /> Create board
        </button>
      </form>

      <div className="grid lg:grid-cols-[minmax(280px,360px)_1fr] gap-4 items-start">
        <div className="border border-dark-border rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-dark-surface border-b border-dark-border text-xs text-dark-text-muted">
            {boardsQuery.isFetching ? 'Loading…' : `${boards.length} board(s)`}
          </div>
          {boardsQuery.error && (
            <p className="p-3 text-sm text-red-400">{boardsQuery.error.message}</p>
          )}
          {!boardsQuery.isFetching && boards.length === 0 && (
            <p className="p-4 text-sm text-dark-text-muted text-center">No boards in this area.</p>
          )}
          <ul className="max-h-[480px] overflow-y-auto divide-y divide-dark-border">
            {boards.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => setSelectedBoardId(b.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-dark-surface/50 transition-colors ${
                    selectedBoardId === b.id ? 'bg-primary/10 border-l-2 border-primary' : ''
                  }`}
                >
                  <p className="font-medium text-dark-text text-sm">#{b.name}</p>
                  <p className="text-xs text-dark-text-muted mt-0.5">
                    id {b.id} · {b.thread_count ?? 0} threads
                    {b.planet_id != null ? ` · planet ${b.planet_id}` : ''}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-4 min-w-0">
          {!selectedBoardId && (
            <p className="text-sm text-dark-text-muted text-center py-12 border border-dashed border-dark-border rounded-lg">
              Select a board from the list to view details and threads.
            </p>
          )}

          {selectedBoardId && (
            <>
              <BoardDetailPanel
                board={boardDetailQuery.data}
                isLoading={boardDetailQuery.isLoading}
                error={boardDetailQuery.error}
              />

              <div>
                <h4 className="text-sm font-medium text-dark-text-secondary mb-2">
                  Threads in this board
                  {boardThreadsQuery.isFetching && ' …'}
                </h4>
                <div className="border border-dark-border rounded-xl overflow-hidden bg-dark-card/30">
                  {boardThreadsQuery.error && (
                    <p className="p-4 text-sm text-red-400">{boardThreadsQuery.error.message}</p>
                  )}
                  {!boardThreadsQuery.isFetching && threads.length === 0 && (
                    <p className="p-8 text-sm text-dark-text-muted text-center">No threads yet.</p>
                  )}
                  {threads.map((thread) => (
                    <ThreadPostCard
                      key={thread.id}
                      thread={thread}
                      actingUserId={ACTING_USER_ID}
                      compact
                      onAdminDetails={setSelectedThreadId}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {selectedThreadId && (
        <ThreadDetailModal
          threadId={selectedThreadId}
          actingUserId={ACTING_USER_ID}
          onClose={() => setSelectedThreadId(null)}
        />
      )}

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-dark-card border border-dark-border rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Create board</h3>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-dark-text-muted">name (no spaces)</label>
                <input
                  className="w-full mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2"
                  placeholder="my-topic-board"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                />
              </div>
              {['planet_id', 'latitude', 'longitude'].map((field) => (
                <div key={field}>
                  <label className="text-dark-text-muted">{field} (optional)</label>
                  <input
                    className="w-full mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2"
                    value={createForm[field]}
                    onChange={(e) => setCreateForm({ ...createForm, [field]: e.target.value })}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                className="px-4 py-2 border border-dark-border rounded"
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-primary text-black rounded font-medium disabled:opacity-50"
                disabled={createMut.isPending || !createForm.name.trim()}
                onClick={() => createMut.mutate()}
              >
                {createMut.isPending ? 'Creating…' : 'Create'}
              </button>
            </div>
            {createMut.error && (
              <p className="text-red-400 text-sm mt-2">{createMut.error.message}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
